import {
  Box,
  Button,
  Center,
  Icon,
  PhoneMockup,
  PhoneMockupLayout,
  Text,
  useToast,
  CountryOption,
} from '@frontend/ui-library';

import { getIdFromUrl } from '@frontend/utils';
import { useEffect, useState } from 'react';
import CountryDropdown from '../../components/CountryDropdown';

import { PageFields_CountryUpdatePage_Fragment as CountryUpdatePageFragment } from '../../graphql/generated';

import { ReactComponent as ArrowRightFilled } from '@frontend/icons/arrow-right-filled.svg';
import { links } from '../../constants';
import { useWidgetContext } from '../../context/WidgetContext';
import { useRegisterHotjarPage } from '../../hooks';
import useUpdateCountry from '../../hooks/useUpdateCountry';
import { BillDetailHeader } from '../../components';
import { isOfframpBill } from '../../utils';

interface CountrySelectionProps {
  data: CountryUpdatePageFragment['pageData'];
}

export default function CountrySelection({ data }: CountrySelectionProps) {
  const {
    countries,
    expiresAt,
    CountryUpdatePageFiatAmount: fiatAmount,
    fiatCurrency,
    merchantName,
    billType,
  } = data;
  const paymentId = getIdFromUrl();
  const isOffRamp = isOfframpBill(billType);
  const [selectedCountryCode, setSelectedCountryCode] = useState<string | null>(
    null
  );
  const [isNewPageLoading, setIsWaitingNextPage] = useState(false);

  const isDropdownSelection = countries && countries.length > 2;
  const haveCountries = countries && countries.length > 0;

  const { configureHeader } = useWidgetContext();
  const { updateCountry, loading } = useUpdateCountry();

  useRegisterHotjarPage('country-selection');
  const toast = useToast();

  useEffect(() => {
    configureHeader({
      allowClosePayment: true,
      expiresAt: expiresAt,
      id: paymentId,
      merchantName,
      billType,
    });
  }, [expiresAt, paymentId, merchantName, billType, configureHeader]);

  const handleCountrySelection = async (countryCode: string) => {
    try {
      const { data } = await updateCountry({
        variables: {
          country: countryCode,
          id: paymentId,
          type: 'bill',
        },
      });

      if (data) {
        setIsWaitingNextPage(true);
      }
    } catch (error) {
      toast.open({
        title: 'Something went wrong',
        message: 'Please try again later',
        status: 'error',
      });
    }
  };

  return (
    <PhoneMockupLayout>
      <PhoneMockup
        header={
          <BillDetailHeader
            amount={fiatAmount}
            currency={fiatCurrency}
            isOffRamp={isOffRamp}
          />
        }
      >
        <Text
          mt="16"
          mb="24"
          as="h1"
          size="huge"
          weight="700"
          color="royal-700"
          align="center"
        >
          Where are you based?
        </Text>
        {isDropdownSelection ? (
          <Box mt="8">
            <CountryDropdown
              countries={countries || []}
              value={selectedCountryCode || ''}
              onValueChange={setSelectedCountryCode}
              testId="country-dropdown"
            />
            <Button
              mt="16"
              fullWidth
              size="large"
              variant="primary"
              loading={loading || isNewPageLoading}
              disabled={!selectedCountryCode}
              data-test-id="proceed-button"
              onClick={() =>
                selectedCountryCode &&
                handleCountrySelection(selectedCountryCode)
              }
            >
              Proceed
            </Button>
          </Box>
        ) : (
          haveCountries &&
          countries.map(
            (country) =>
              country?.alpha2code &&
              country.name && (
                <Box key={country.alpha2code} mt="8">
                  <CountryOption
                    key={country.alpha2code}
                    data-test-id="country-option"
                    onClick={() => {
                      if (!country.alpha2code) {
                        return;
                      }

                      handleCountrySelection(country.alpha2code);
                      setSelectedCountryCode(country.alpha2code);
                    }}
                    disabled={loading || isNewPageLoading}
                    loading={
                      selectedCountryCode === country.alpha2code &&
                      (loading || isNewPageLoading)
                    }
                    countryFlagSrc={`assets/countryFlags/${country.alpha2code.toLowerCase()}.svg`}
                    countryName={country.name}
                  />
                </Box>
              )
          )
        )}
        <Center mt="auto" direction="column">
          <Text as="div" color="neutral-600" size="small">
            Don't see your country?
          </Text>
          <Text
            as="a"
            decoration="none"
            href={links.support.MERCHANT_ELIGIBILITY_AND_RESTRICTED_COUNTRIES}
            target="_blank"
            rel="noopener noreferrer"
            size="tiny-callout"
            mt="8"
            weight="600"
            color="royal-500"
          >
            <Center>
              Learn more
              <Box ml="4">
                <Center>
                  <Icon size={12} component={<ArrowRightFilled />} />
                </Center>
              </Box>
            </Center>
          </Text>
        </Center>
      </PhoneMockup>
    </PhoneMockupLayout>
  );
}
